'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, LogIn, Copy, Check } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';

interface LoginData {
  email: string;
  password: string;
  remember: boolean;
}

const demoCredentials = [
  { id: 'cred-hr', role: 'HR Admin', email: 'hr.admin@mm-mvshahacademy.ac.ke', password: 'HRAdmin@2026', redirect: '/hr-dashboard' },
  { id: 'cred-viewer', role: 'HR Viewer', email: 'hr.viewer@mm-mvshahacademy.ac.ke', password: 'HRView@2026', redirect: '/hr-dashboard' },
  { id: 'cred-applicant', role: 'Applicant', email: 'amina.ochieng@gmail.com', password: 'Applicant@2026', redirect: '/application-form' },
];

interface LoginFormProps {
  onSwitch: () => void;
}

export default function LoginForm({ onSwitch }: LoginFormProps) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<LoginData>();

  const onSubmit = async (data: LoginData) => {
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/login with { email, password }
    await new Promise(r => setTimeout(r, 900));

    const matched = demoCredentials.find(
      c => c.email === data.email && c.password === data.password
    );

    if (matched) {
      toast.success(`Welcome back! Signed in as ${matched.role}.`);
      router.push(matched.redirect);
    } else {
      toast.error('Invalid credentials — use the demo accounts below to sign in');
      setIsLoading(false);
    }
  };

  const handleCopy = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleUseCreds = (cred: typeof demoCredentials[0]) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-7">
        <h1 className="text-2xl font-700 text-foreground mb-1" style={{ fontWeight: 700 }}>
          Sign In
        </h1>
        <p className="text-sm text-muted-foreground">
          Access your application or HR dashboard.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="form-label">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            className={`form-input ${errors.email ? 'error' : ''}`}
            placeholder="you@example.com"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
            })}
          />
          {errors.email && <p className="form-error">{errors.email.message}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="form-label" style={{ marginBottom: 0 }}>
              Password <span className="text-red-500">*</span>
            </label>
            <button type="button" className="text-xs text-primary hover:underline font-500" style={{ color: 'var(--primary)' }}>
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className={`form-input pr-10 ${errors.password ? 'error' : ''}`}
              placeholder="Enter your password"
              {...register('password', { required: 'Password is required' })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="form-error">{errors.password.message}</p>}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="remember"
            className="w-4 h-4 rounded border-input accent-primary"
            {...register('remember')}
          />
          <label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
            Remember me for 30 days
          </label>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-2.5"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Signing in...
            </>
          ) : (
            <>
              <LogIn size={16} />
              Sign In
            </>
          )}
        </button>
      </form>

      <div className="mt-5 text-center">
        <p className="text-sm text-muted-foreground">
          Don&apos;t have an account?{' '}
          <button onClick={onSwitch} className="text-primary font-600 hover:underline" style={{ color: 'var(--primary)' }}>
            Create Account
          </button>
        </p>
      </div>

      {/* Demo credentials */}
      <div className="mt-6 rounded-lg border border-border overflow-hidden">
        <div className="bg-secondary px-4 py-2.5 flex items-center justify-between">
          <span className="text-xs font-600 text-muted-foreground uppercase tracking-wider">Demo Accounts</span>
        </div>
        <div className="divide-y divide-border">
          {demoCredentials.map((cred) => (
            <div key={cred.id} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-secondary/50 transition-colors">
              <div className="min-w-0">
                <div className="text-xs font-700 text-primary mb-0.5" style={{ fontWeight: 700, color: 'var(--primary)' }}>
                  {cred.role}
                </div>
                <div className="text-xs text-muted-foreground truncate">{cred.email}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => handleCopy(cred.password, cred.id)}
                  className="p-1.5 rounded-md hover:bg-border transition-colors"
                  title="Copy password"
                >
                  {copiedField === cred.id ? <Check size={13} className="text-green-600" /> : <Copy size={13} className="text-muted-foreground" />}
                </button>
                <button
                  type="button"
                  onClick={() => handleUseCreds(cred)}
                  className="text-xs font-600 px-3 py-1.5 rounded-md border border-border hover:bg-secondary transition-colors"
                  style={{ color: 'var(--primary)' }}
                >
                  Use
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}